import { fetchLlmModels, fetchLlmPolicy, selectLlmKeyById } from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useLoader, useQuery, useSelector } from "@app/react";
import { environmentLlmKeysUrl } from "@app/routes";
import { usePaginate, useSyncedQueryParam } from "@app/ui/hooks";
import { CopyText } from "@app/ui/shared/copy";
import { Group } from "@app/ui/shared/group";
import {
  IconCheckCircle,
  IconChevronDown,
  IconXCircle,
} from "@app/ui/shared/icons";
import { InputSearch } from "@app/ui/shared/input";
import {
  DescBar,
  FilterBar,
  LoadingBar,
  PaginateBar,
} from "@app/ui/shared/resource-list-view";
import { EmptyTr, TBody, THead, Table, Td, Th, Tr } from "@app/ui/shared/table";
import { tokens } from "@app/ui/shared/tokens";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

interface LlmModel {
  id: string;
  supported: boolean;
}

const SortIcon = () => (
  <div className="inline-block">
    <IconChevronDown
      variant="sm"
      className="top-1 -ml-1 relative group-hover:opacity-100 opacity-50"
    />
  </div>
);

interface ModelCellProps {
  model: LlmModel;
}

const ModelNameCell = ({ model }: ModelCellProps) => {
  return (
    <Td>
      <CopyText text={model.id}>
        <span className={tokens.type.darker}>{model.id}</span>
      </CopyText>
    </Td>
  );
};

const ModelSupportedCell = ({ model }: ModelCellProps) => {
  return (
    <Td>
      {model.supported ? (
        <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
          <IconCheckCircle variant="sm" color="#00633f" /> Yes
        </div>
      ) : (
        <div className="flex items-center gap-2 text-red-500 text-sm font-medium">
          <IconXCircle variant="sm" color="#901717" /> No
        </div>
      )}
    </Td>
  );
};

export const LlmKeyModelsPage = () => {
  const { id = "" } = useParams();
  const { search, onChange } = useSyncedQueryParam();
  const [sortBy, setSortBy] = useState<keyof LlmModel>("id");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const llmKey = useSelector((s) => selectLlmKeyById(s, { id }));
  const orgId = useSelector(selectOrganizationSelectedId);
  const envId = llmKey.environmentId ?? "";

  useQuery(fetchLlmModels());
  const loader = useLoader(fetchLlmModels);

  const policyPayload = useMemo(
    () => ({ envId, orgId: orgId ?? "" }),
    [envId, orgId],
  );
  useQuery(fetchLlmPolicy(policyPayload));
  const policyLoader = useLoader(fetchLlmPolicy(policyPayload));
  const policyModels = policyLoader.meta?.policy?.models;

  const allModels: LlmModel[] = loader.meta?.models || [];

  const modelsToShow: LlmModel[] =
    Array.isArray(policyModels) && policyModels.length > 0
      ? (() => {
          const seen = new Set<string>();
          const result: LlmModel[] = [];
          for (const modelId of policyModels) {
            if (modelId.includes("*")) {
              const prefix = modelId.slice(0, modelId.indexOf("*"));
              for (const m of allModels) {
                if (m.id.startsWith(prefix) && !seen.has(m.id)) {
                  seen.add(m.id);
                  result.push(m);
                }
              }
            } else {
              const found = allModels.find((m) => m.id === modelId);
              const entry = found ?? { id: modelId, supported: false };
              if (!seen.has(entry.id)) {
                seen.add(entry.id);
                result.push(entry);
              }
            }
          }
          return result;
        })()
      : allModels;

  const filteredModels = modelsToShow.filter((model) =>
    model.id.toLowerCase().includes(search.toLowerCase()),
  );

  // Sort models
  const sortedModels = [...filteredModels].sort((a, b) => {
    const aVal = a[sortBy];
    const bVal = b[sortBy];

    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortDir === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    if (typeof aVal === "boolean" && typeof bVal === "boolean") {
      const aNum = aVal ? 1 : 0;
      const bNum = bVal ? 1 : 0;
      return sortDir === "asc" ? aNum - bNum : bNum - aNum;
    }

    return 0;
  });

  // Paginate results
  const paginated = usePaginate(sortedModels);

  const onSort = (key: keyof LlmModel) => {
    if (key === sortBy) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  return (
    <Group>
      <Group size="sm">
        <FilterBar>
          <div className="flex justify-between">
            <Group variant="horizontal" size="sm" className="items-center">
              <InputSearch
                placeholder="Search models..."
                search={search}
                onChange={onChange}
              />
              <LoadingBar isLoading={loader.isLoading} />
            </Group>
          </div>

          <Group
            variant="horizontal"
            size="lg"
            className="items-center mt-1 flex-wrap justify-between gap-2"
          >
            <Group variant="horizontal" size="lg" className="items-center">
              <DescBar>{paginated.totalItems} models</DescBar>
              <PaginateBar {...paginated} />
            </Group>
            {envId ? (
              <span className="text-sm text-gray-600">
                Model access is defined within the Environment's{" "}
                <Link
                  to={environmentLlmKeysUrl(envId)}
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  LLM Gateway settings
                </Link>
                .
              </span>
            ) : null}
          </Group>
        </FilterBar>
      </Group>

      <Table>
        <THead>
          <Th
            className="cursor-pointer hover:text-black group"
            onClick={() => onSort("id")}
          >
            Model Name <SortIcon />
          </Th>
          <Th
            className="cursor-pointer hover:text-black group"
            onClick={() => onSort("supported")}
          >
            Supported <SortIcon />
          </Th>
        </THead>

        <TBody>
          {paginated.data.length === 0 ? (
            <EmptyTr colSpan={2} />
          ) : (
            paginated.data.map((model) => (
              <Tr key={model.id}>
                <ModelNameCell model={model} />
                <ModelSupportedCell model={model} />
              </Tr>
            ))
          )}
        </TBody>
      </Table>
      {paginated.totalPages > 1 && <PaginateBar {...paginated} />}
    </Group>
  );
};
